import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Star, Eye, EyeOff, ImagePlus, RefreshCw, Trash2, PlayCircle, Pencil, Search } from "lucide-react";
import { api, apiError, fmtDate, API } from "../lib/api";
import { PageHeader } from "./AdminLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

const STATUS = { brouillon: { label: "Brouillon", cls: "bg-amber-100 text-amber-800" }, publie: { label: "Publié", cls: "bg-green-100 text-green-800" }, archive: { label: "Dépublié", cls: "bg-gray-200 text-gray-700" } };
const PLACEMENTS = { accueil: "Accueil (vidéo principale possible)", bandeau: "Bandeau promotionnel", galerie: "Galerie" };

export default function Media() {
    const [data, setData] = useState(null);
    const [tab, setTab] = useState("video");
    const [statusFilter, setStatusFilter] = useState("");
    const [search, setSearch] = useState("");
    const [uploadOpen, setUploadOpen] = useState(false);
    const [form, setForm] = useState({ title: "", description: "", placement: "accueil" });
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState(0);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteRefs, setDeleteRefs] = useState(null);
    const [metaTarget, setMetaTarget] = useState(null);
    const [metaForm, setMetaForm] = useState({ alt: "", caption: "", focal_x: 50, focal_y: 50 });
    const thumbInput = useRef({});
    const replaceInput = useRef({});

    const load = useCallback(
        () =>
            api
                .get("/admin/media", { params: { kind: tab, status: statusFilter || undefined, search: search || undefined, limit: 200 } })
                .then((r) => setData(r.data))
                .catch((e) => toast.error(apiError(e))),
        [tab, statusFilter, search]
    );
    useEffect(() => { load(); }, [load]);

    const upload = async () => {
        if (!file) return toast.error("Sélectionnez un fichier.");
        setBusy(true);
        setProgress(0);
        const fd = new FormData();
        fd.append("file", file);
        fd.append("title", form.title);
        fd.append("description", form.description);
        fd.append("placement", form.placement);
        try {
            await api.post("/admin/media", fd, { onUploadProgress: (e) => setProgress(Math.round((e.loaded * 100) / (e.total || 1))) });
            toast.success("Média importé (statut : brouillon).");
            setUploadOpen(false);
            setFile(null);
            setForm({ title: "", description: "", placement: "accueil" });
            load();
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setBusy(false);
        }
    };

    const patch = async (m, data, msg) => {
        try {
            await api.patch(`/admin/media/${m.media_id}`, data);
            toast.success(msg);
            load();
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    const sendFile = async (m, f, kind) => {
        if (!f) return;
        const fd = new FormData();
        fd.append("file", f);
        try {
            await api.post(`/admin/media/${m.media_id}/${kind}`, fd);
            toast.success(kind === "thumbnail" ? "Miniature enregistrée." : "Fichier remplacé.");
            load();
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    const openDelete = async (m) => {
        setDeleteTarget(m);
        setDeleteRefs(null);
        try {
            const r = await api.get(`/admin/media/${m.media_id}/references`);
            setDeleteRefs(r.data.references || []);
        } catch {
            setDeleteRefs([]);
        }
    };

    const items = data?.items || [];

    return (
        <div data-testid="admin-media">
            <PageHeader title="Médiathèque" subtitle="Source unique des images et vidéos du site. Les médias publiés sont sélectionnables dans chaque section (Contenu du site).">
                <Button onClick={() => setUploadOpen(true)} className="bg-forest text-cream gap-2" data-testid="upload-media-button"><Upload size={16} /> Importer un média</Button>
            </PageHeader>
            <div className="flex flex-wrap gap-2 mb-6 items-center">
                {["video", "image"].map((t) => (
                    <button key={t} onClick={() => setTab(t)} className={`px-4 py-1.5 rounded-full text-sm border ${tab === t ? "bg-forest text-cream border-forest" : "border-border text-pineneedle"}`} data-testid={`media-tab-${t}`}>
                        {t === "video" ? "Vidéos" : "Images"}
                    </button>
                ))}
                <span className="w-px h-5 bg-border mx-1 hidden sm:block" />
                {[["", "Tous"], ["publie", "Publiés"], ["brouillon", "Brouillons"], ["archive", "Dépubliés"]].map(([v, l]) => (
                    <button key={v} onClick={() => setStatusFilter(v)} className={`px-3 py-1.5 rounded-full text-xs border ${statusFilter === v ? "bg-gold/20 text-gold-dark border-gold/50" : "border-border text-pineneedle"}`} data-testid={`media-status-filter-${v || "all"}`}>
                        {l}
                    </button>
                ))}
                <div className="relative flex-1 min-w-44">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-pineneedle/50" />
                    <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher…" className="pl-8 bg-white" data-testid="media-search" />
                </div>
            </div>
            {!data ? (
                <p className="text-pineneedle text-sm">Chargement…</p>
            ) : items.length === 0 ? (
                <p className="rounded-2xl border-2 border-dashed border-gold/40 bg-white p-10 text-center text-pineneedle" data-testid="media-empty">
                    Aucun {tab === "video" ? "vidéo" : "visuel"} dans cette vue. Importez un média ou changez de filtre.
                </p>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    {items.map((m) => (
                        <article key={m.media_id} className="rounded-2xl bg-white border border-gold/25 p-5 shadow-sm" data-testid={`media-card-${m.media_id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <h3 className="font-heading font-bold text-forest">{m.title}</h3>
                                    <p className="text-xs text-pineneedle mt-0.5">
                                        {m.mime} · {(m.size_bytes / 1048576).toFixed(1)} Mo{m.width ? ` · ${m.width}×${m.height}` : ""} · {PLACEMENTS[m.placement] || m.placement} · {m.plays || 0} lecture(s)
                                    </p>
                                </div>
                                <span className={`text-xs px-2 py-1 rounded-full shrink-0 ${STATUS[m.status]?.cls}`} data-testid={`media-status-${m.media_id}`}>{STATUS[m.status]?.label}</span>
                            </div>
                            {m.kind === "video" ? (
                                <video controls preload="metadata" playsInline className="w-full rounded-xl mt-3 bg-forest-deep aspect-video" src={`${API}/media/file/${m.media_id}`} poster={m.has_thumbnail ? `${API}/media/thumb/${m.media_id}` : undefined} data-testid={`media-preview-${m.media_id}`} />
                            ) : (
                                <img src={`${API}/media/file/${m.media_id}`} alt={m.alt || m.title} className="w-full rounded-xl mt-3 object-cover max-h-56" data-testid={`media-preview-${m.media_id}`} />
                            )}
                            {m.error && <p className="text-xs text-destructive mt-2">Erreur de traitement : {m.error}</p>}
                            <div className="flex flex-wrap gap-1.5 mt-4">
                                {m.status !== "publie" ? (
                                    <Button size="sm" className="h-8 text-xs bg-forest text-cream gap-1" onClick={() => patch(m, { status: "publie" }, "Média publié.")} data-testid={`publish-${m.media_id}`}><Eye size={13} /> Publier</Button>
                                ) : (
                                    <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => patch(m, { status: "archive" }, "Média dépublié.")} data-testid={`unpublish-${m.media_id}`}><EyeOff size={13} /> Dépublier</Button>
                                )}
                                {m.kind === "video" && !m.is_primary && (
                                    <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => patch(m, { is_primary: true }, "Vidéo principale définie et publiée.")} data-testid={`primary-${m.media_id}`}><Star size={13} /> Vidéo principale</Button>
                                )}
                                {m.is_primary && <span className="text-xs text-gold font-semibold flex items-center gap-1 px-2"><PlayCircle size={13} /> Vidéo principale du site</span>}
                                <Button size="sm" variant="outline" className="h-8 text-xs gap-1" aria-label={`Métadonnées de ${m.title}`} title="Métadonnées" onClick={() => { setMetaTarget(m); setMetaForm({ alt: m.alt || "", caption: m.caption || "", focal_x: m.focal_x ?? 50, focal_y: m.focal_y ?? 50 }); }} data-testid={`meta-${m.media_id}`}><Pencil size={13} /> Infos</Button>
                                <Button size="sm" variant="outline" className="h-8 text-xs gap-1" aria-label={`Miniature de ${m.title}`} title="Miniature" onClick={() => thumbInput.current[m.media_id]?.click()} data-testid={`thumb-${m.media_id}`}><ImagePlus size={13} /> Miniature</Button>
                                <Button size="sm" variant="outline" className="h-8 text-xs gap-1" aria-label={`Remplacer le fichier de ${m.title}`} title="Remplacer le fichier" onClick={() => replaceInput.current[m.media_id]?.click()} data-testid={`replace-${m.media_id}`}><RefreshCw size={13} /> Remplacer</Button>
                                <Button size="sm" variant="outline" className="h-8 text-xs gap-1 text-destructive border-destructive/40" aria-label={`Supprimer ${m.title}`} title="Supprimer" onClick={() => openDelete(m)} data-testid={`delete-media-${m.media_id}`}><Trash2 size={13} /></Button>
                            </div>
                            <input ref={(el) => (thumbInput.current[m.media_id] = el)} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => sendFile(m, e.target.files[0], "thumbnail")} />
                            <input ref={(el) => (replaceInput.current[m.media_id] = el)} type="file" accept={m.kind === "video" ? "video/mp4,video/webm" : "image/jpeg,image/png,image/webp"} className="hidden" onChange={(e) => sendFile(m, e.target.files[0], "replace")} />
                            <p className="text-[11px] text-pineneedle/60 mt-3">Importé par {m.uploaded_by} le {fmtDate(m.created_at)}</p>
                        </article>
                    ))}
                </div>
            )}

            <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
                <DialogContent className="bg-cream" data-testid="upload-dialog">
                    <DialogHeader><DialogTitle className="font-heading text-forest">Importer un média</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Fichier (MP4, WebM jusqu'à 500 Mo — JPEG, PNG, WebP jusqu'à 10 Mo)</Label>
                            <Input type="file" accept="video/mp4,video/webm,image/jpeg,image/png,image/webp" onChange={(e) => setFile(e.target.files[0])} data-testid="upload-file-input" />
                            {file && <p className="text-xs text-pineneedle mt-1">{(file.size / 1048576).toFixed(1)} Mo — {file.name}</p>}
                        </div>
                        <div><Label>Titre</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="upload-title-input" /></div>
                        <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="upload-description-input" /></div>
                        <div>
                            <Label>Emplacement</Label>
                            <Select value={form.placement} onValueChange={(v) => setForm({ ...form, placement: v })}>
                                <SelectTrigger data-testid="upload-placement-select"><SelectValue /></SelectTrigger>
                                <SelectContent>{Object.entries(PLACEMENTS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        {busy && (
                            <div className="h-2 rounded-full bg-pearl overflow-hidden" data-testid="upload-progress">
                                <div className="h-full bg-gold transition-all" style={{ width: `${progress}%` }} />
                            </div>
                        )}
                        <Button onClick={upload} disabled={busy} className="w-full bg-forest text-cream" data-testid="upload-submit">{busy ? `Import en cours… ${progress}%` : "Importer"}</Button>
                    </div>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
                <AlertDialogContent className="bg-cream" data-testid="media-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer « {deleteTarget?.title} » ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {deleteRefs === null
                                ? "Vérification des références…"
                                : deleteRefs.length > 0
                                  ? `Suppression impossible : ce média est utilisé par ${deleteRefs.length} section(s) : ${deleteRefs.map((r) => `« ${r.key} » (${r.where})`).join(", ")}. Retirez d'abord l'association dans Contenu du site.`
                                  : "Aucune section n'utilise ce média. Il sera retiré du site immédiatement."}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="media-delete-cancel">Annuler</AlertDialogCancel>
                        {deleteRefs && deleteRefs.length === 0 && (
                            <AlertDialogAction className="bg-destructive text-white" onClick={async () => { try { await api.delete(`/admin/media/${deleteTarget.media_id}`); toast.success("Média supprimé."); load(); } catch (e) { toast.error(apiError(e)); } }} data-testid="media-delete-confirm">Supprimer</AlertDialogAction>
                        )}
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <Dialog open={!!metaTarget} onOpenChange={() => setMetaTarget(null)}>
                <DialogContent className="bg-cream" data-testid="media-meta-dialog">
                    <DialogHeader><DialogTitle className="font-heading text-forest">Métadonnées — {metaTarget?.title}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div><Label>Texte alternatif (accessibilité, SEO)</Label><Input value={metaForm.alt} onChange={(e) => setMetaForm({ ...metaForm, alt: e.target.value })} data-testid="media-alt-input" /></div>
                        <div><Label>Légende</Label><Input value={metaForm.caption} onChange={(e) => setMetaForm({ ...metaForm, caption: e.target.value })} data-testid="media-caption-input" /></div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><Label>Point focal horizontal ({metaForm.focal_x} %)</Label><input type="range" min="0" max="100" value={metaForm.focal_x} onChange={(e) => setMetaForm({ ...metaForm, focal_x: parseInt(e.target.value) })} className="w-full accent-gold" data-testid="media-focal-x" /></div>
                            <div><Label>Point focal vertical ({metaForm.focal_y} %)</Label><input type="range" min="0" max="100" value={metaForm.focal_y} onChange={(e) => setMetaForm({ ...metaForm, focal_y: parseInt(e.target.value) })} className="w-full accent-gold" data-testid="media-focal-y" /></div>
                        </div>
                        <Button className="w-full bg-forest text-ivory" onClick={async () => { try { await api.patch(`/admin/media/${metaTarget.media_id}`, metaForm); toast.success("Métadonnées enregistrées."); setMetaTarget(null); load(); } catch (e) { toast.error(apiError(e)); } }} data-testid="media-meta-save">Enregistrer</Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
